const STORAGE_KEY = "castaway.longArcState.v1";

interface PersistedState {
  simDayCount: number;
}

/**
 * Persists the simulated day count across visits via localStorage, gating
 * `minSimDay`/`maxSimDay` on `VignetteDefinition` (see vignette-defs/
 * types.ts and SceneDirector.isEligible — wired up as of Phase 5). Raft
 * building unlocks a few sim-days in; the escape-ending vignette unlocks
 * further out and calls `reset()` on exit, looping the arc back to day 0 —
 * a new raft has to be built again, mirroring the original concept's
 * cyclical restart rather than a true "new game."
 */
export class LongArcState {
  private simDayCount: number;

  constructor() {
    this.simDayCount = this.load();
  }

  getSimDayCount(): number {
    return this.simDayCount;
  }

  advanceDay(): void {
    this.simDayCount += 1;
    this.save();
  }

  /** Loops the long arc back to day 0 — called when the escape-ending vignette concludes. */
  reset(): void {
    this.simDayCount = 0;
    this.save();
  }

  private load(): number {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return 0;
      const parsed = JSON.parse(raw) as Partial<PersistedState>;
      return typeof parsed.simDayCount === "number" ? parsed.simDayCount : 0;
    } catch {
      return 0; // corrupted or unavailable storage (private browsing) — start fresh rather than throw
    }
  }

  private save(): void {
    try {
      const state: PersistedState = { simDayCount: this.simDayCount };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // localStorage unavailable/full — non-fatal, just don't persist this time.
    }
  }
}
